import { useEffect, useRef, useState } from "react";
import Modal from "./Modal";
import { Mic, MicOff, Video, VideoOff, PhoneOff, Maximize, X } from "./icons";
import { CallSession, type CallStatus, type ChatMsg } from "../lib/antMediaCall";
import type { CallConfig } from "../api";

interface Props {
  open: boolean;
  onClose: () => void;
  loName: string;
  loHeadshotUrl: string | null;
  enabled: boolean;       // when false the parent disables the trigger button entirely
  callConfig: CallConfig;
  onBook?: () => void;    // when busy, jump straight to the booking modal
}

export default function VideoCallModal(props: Props) {
  if (!props.open) return null;
  // The parent gates the trigger button on `enabled` so we never open while offline.
  // If we ever do (race condition, programmatic open), we still skip the modal.
  if (!props.enabled) return null;
  return <RealCall {...props} />;
}

// ────────────────────────────────────────────────────────────
// Real Ant Media call (used when video_call_enabled flag is on)
// ────────────────────────────────────────────────────────────
function RealCall({ open, onClose, loName, loHeadshotUrl, callConfig, onBook }: Props) {
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const sessionRef = useRef<CallSession | null>(null);

  const [status, setStatus] = useState<CallStatus>("idle");
  const [statusDetail, setStatusDetail] = useState<string>("");
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [draft, setDraft] = useState("");
  const [hasRemote, setHasRemote] = useState(false);

  useEffect(() => {
    const session = new CallSession(callConfig, {
      onStatus: (s, detail) => { setStatus(s); if (detail) setStatusDetail(detail); },
      onLocalStream: (stream) => {
        if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      },
      onRemoteStream: (stream) => {
        setHasRemote(!!stream && stream.getTracks().length > 0);
        if (remoteVideoRef.current) remoteVideoRef.current.srcObject = stream;
      },
      onChat: (msg) => setMessages((m) => [...m, msg]),
      onError: (err) => setStatusDetail(err),
    });
    sessionRef.current = session;
    void session.start();
    return () => {
      session.end();
      sessionRef.current = null;
    };
  }, [callConfig]);

  // When the LO hangs up (or the connection drops), auto-close the modal after
  // a brief moment so the consumer sees a clean "Call ended" beat rather than
  // a frozen call view. "busy" is excluded — that has its own dedicated screen.
  useEffect(() => {
    if (status !== "ended") return;
    const t = window.setTimeout(() => onClose(), 1800);
    return () => window.clearTimeout(t);
  }, [status, onClose]);

  function toggleMute() { const next = !muted; setMuted(next); sessionRef.current?.toggleAudio(next); }
  function toggleCam()  { const next = !camOff; setCamOff(next); sessionRef.current?.toggleVideo(next); }
  function endCall()    { sessionRef.current?.end(); onClose(); }
  function sendChat() {
    const t = draft.trim();
    if (!t) return;
    sessionRef.current?.sendChat(t);
    setDraft("");
  }

  // Dedicated busy screen: LO is on another 1:1 call. Surface the booking CTA
  // so the consumer has a clear next step instead of a dead end.
  if (status === "busy") {
    return (
      <Modal open={open} onClose={onClose} size="md">
        <div className="busy">
          {loHeadshotUrl ? (
            <img src={loHeadshotUrl} alt={loName} className="busy__photo" />
          ) : (
            <div className="busy__photo busy__photo--placeholder">{loName.charAt(0)}</div>
          )}
          <div className="busy__title">{loName} is on another call</div>
          <div className="busy__sub">
            They can only take one call at a time. Try again in a few minutes,
            or book a consultation for a guaranteed time.
          </div>
          <div className="busy__actions">
            {onBook && (
              <button
                className="btn btn--primary btn--lg"
                onClick={() => { onClose(); onBook(); }}
              >
                Book a consultation
              </button>
            )}
            <button className="btn btn--ghost btn--lg" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={endCall} size="xl">
      <div className="call">
        <div className="call__stage">
          <video
            ref={remoteVideoRef}
            className="call__remote-video"
            autoPlay
            playsInline
            // Hide until we're actually in-call -- before lo_answered the
            // remote tracks are disabled so the element would just be black.
            style={{ display: status === "in_call" && hasRemote ? "block" : "none" }}
          />
          {(status !== "in_call" || !hasRemote) && (
            <div className="call__remote">
              {loHeadshotUrl ? (
                <img src={loHeadshotUrl} alt={loName} className="call__remote-photo" />
              ) : (
                <div className="call__remote-photo call__remote-photo--placeholder">
                  {loName.charAt(0)}
                </div>
              )}
              <div className="call__remote-name">{loName}</div>
              <div className="call__remote-status">
                <span className="call__dot" />
                <span>{statusLabel(status, statusDetail)}</span>
              </div>
            </div>
          )}

          <div className="call__local">
            <video
              ref={localVideoRef}
              className="call__local-video"
              autoPlay
              playsInline
              muted
              style={{ display: camOff ? "none" : "block" }}
            />
            {camOff && (
              <div className="call__local-off">
                <VideoOff size={20} />
                <span>Camera off</span>
              </div>
            )}
          </div>

          {chatOpen && (
            <ChatPanel
              messages={messages}
              draft={draft}
              setDraft={setDraft}
              onSend={sendChat}
              onClose={() => setChatOpen(false)}
            />
          )}
        </div>

        <div className="call__bar">
          <button
            className={`call-btn ${muted ? "is-active" : ""}`}
            onClick={toggleMute}
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? <MicOff size={20} /> : <Mic size={20} />}
          </button>
          <button
            className={`call-btn ${camOff ? "is-active" : ""}`}
            onClick={toggleCam}
            aria-label={camOff ? "Turn camera on" : "Turn camera off"}
          >
            {camOff ? <VideoOff size={20} /> : <Video size={20} />}
          </button>
          <button
            className={`call-btn ${chatOpen ? "is-active-soft" : ""}`}
            onClick={() => setChatOpen(!chatOpen)}
            aria-label="Chat"
          >
            <ChatBubble />
          </button>
          <button className="call-btn call-btn--ghost" aria-label="Fullscreen">
            <Maximize size={18} />
          </button>
          <button className="call-btn call-btn--end" onClick={endCall} aria-label="End call">
            <PhoneOff size={20} />
          </button>
        </div>

        <div className="call__caption">{statusLabel(status, statusDetail)}</div>
      </div>
    </Modal>
  );
}

function statusLabel(s: CallStatus, detail: string): string {
  switch (s) {
    case "idle": return "Initializing…";
    case "requesting_media": return "Requesting camera and microphone access…";
    case "connecting": return "Connecting to call server…";
    case "publishing": return "Connecting…";
    case "waiting_remote": return detail || "Waiting for the loan officer to join…";
    case "ringing": return detail || "Calling…";
    case "in_call": return "Connected";
    case "busy": return detail || "Loan officer is on another call.";
    case "ended": return "Call ended";
    case "error": return detail || "Something went wrong.";
  }
}

// ────────────────────────────────────────────────────────────
// Chat panel (shared)
// ────────────────────────────────────────────────────────────
function ChatPanel({
  messages,
  draft,
  setDraft,
  onSend,
  onClose,
}: {
  messages: ChatMsg[];
  draft: string;
  setDraft: (v: string) => void;
  onSend: () => void;
  onClose: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  return (
    <aside className="chat-panel">
      <div className="chat-panel__head">
        <div className="chat-panel__title">In-call chat</div>
        <button className="icon-btn icon-btn--sm" onClick={onClose} aria-label="Close chat">
          <X size={14} />
        </button>
      </div>
      <div className="chat-panel__list" ref={scrollRef}>
        {messages.length === 0 && (
          <div className="chat-panel__empty">Say hi 👋</div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`chat-msg chat-msg--${m.from}`}>
            <div className="chat-msg__bubble">{m.text}</div>
          </div>
        ))}
      </div>
      <form
        className="chat-panel__input"
        onSubmit={(e) => { e.preventDefault(); onSend(); }}
      >
        <input
          type="text"
          placeholder="Type a message…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <button type="submit" className="btn btn--secondary btn--sm" disabled={!draft.trim()}>
          Send
        </button>
      </form>
    </aside>
  );
}

function ChatBubble() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}
